import React from 'react'
import { Box, Button, Divider, Paper, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RedeemIcon from '@mui/icons-material/Redeem'
import { formatNumber } from 'src/common/helpers'
import { PortalMetrics } from 'src/common/api'
import { formatRate, QCAD, TOOLTIPS } from '../../utils'

interface MetricsHeaderProps {
  metrics: PortalMetrics
  onClaim: (statementId: string) => void
}

const Label: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = 'text.secondary' }) => (
  <Typography
    component="div"
    sx={{
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      color,
      display: 'flex',
      alignItems: 'center'
    }}
  >
    {children}
  </Typography>
)

const InfoTip: React.FC<{ text: string; stopToggle?: boolean }> = ({ text, stopToggle }) => (
  <Tooltip title={text} arrow>
    <InfoOutlinedIcon
      onClick={stopToggle ? (e) => e.stopPropagation() : undefined}
      sx={{ fontSize: 15, ml: 0.5, color: 'text.secondary', cursor: 'default' }}
    />
  </Tooltip>
)

const MetricValue: React.FC<{ value: number; color?: string; size?: number }> = ({
  value,
  color = 'text.primary',
  size = 26
}) => (
  <Typography sx={{ fontFamily: 'monospace', fontSize: size, fontWeight: 800, color, letterSpacing: '-0.5px' }}>
    {formatNumber(value)}{' '}
    <Box component="span" sx={{ fontSize: 15, fontWeight: 600 }}>
      {QCAD}
    </Box>
  </Typography>
)

const MetricsHeader: React.FC<MetricsHeaderProps> = ({ metrics, onClaim }) => {
  const { unclaimed } = metrics

  return (
    <Paper
      variant="card"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
        borderRadius: 0,
        overflow: 'hidden',
        mb: 3
      }}
    >
      {unclaimed && (
        <>
          <Box
            sx={{
              flex: 1.2,
              p: 3.5,
              borderLeft: '4px solid #f59e0b',
              backgroundColor: '#fffbeb',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 1.25
            }}
          >
            <Label color="#b45309">Unclaimed Revenue Share</Label>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
              {unclaimed.period} statement is ready to claim
            </Typography>
            <MetricValue value={unclaimed.amountQcad} color="#92400e" size={24} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              <strong>{unclaimed.revSharingWalletName}</strong> upon approval
            </Typography>
            <Button
              variant="contained"
              startIcon={<RedeemIcon />}
              onClick={() => onClaim(unclaimed.statementId)}
              sx={{ width: 'fit-content', mt: 0.5 }}
            >
              Mint Earnings
            </Button>
          </Box>
          <Divider orientation="vertical" flexItem />
        </>
      )}

      <Box sx={{ flex: 1.2, p: 3.5, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Label color="primary.main">
          Gross QCAD Earned ({metrics.currentPeriodLabel} MTD)
          <InfoTip text={TOOLTIPS.grossAccrual} />
        </Label>
        <Box mt={1}>
          <MetricValue value={metrics.netRevenuePayoutMtdQcad} color="primary.main" size={30} />
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem />

      <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Label>
          Net Rate (Annualized)
          <InfoTip text={TOOLTIPS.effectiveRate} />
        </Label>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 24, fontWeight: 800, color: 'primary.main', mt: 1 }}>
          {formatRate(metrics.effectiveRate)}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem />

      <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Label>
          Avg. Daily Balance (QCAD) / MTD
          <InfoTip text={TOOLTIPS.avgDailyBalance} />
        </Label>
        <Box mt={1}>
          <MetricValue value={metrics.avgDailyBalanceMtdQcad} size={24} />
        </Box>
      </Box>
    </Paper>
  )
}

export default MetricsHeader
